/**
 * DV-PROJ-001: Setting Profiles Section Component
 *
 * Manages setting profiles for a project including:
 * - Setting profile CRUD
 * - Reference image upload
 * - 9:16 preview display
 * - Default setting selection
 */

import React, { useState } from 'react';
import {
  Plus,
  MapPin,
  Star,
  StarOff,
  Upload,
  MoreHorizontal,
  Edit2,
  Trash2,
  ImageIcon
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  useLocationProfilesQuery,
  useUpdateLocationProfileMutation,
  useDeleteLocationProfileMutation
} from '@/hooks/useProjectsQuery';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import { Skeleton } from '@/components/ui/LoadingSkeletons';
import CreateSettingModal from '../modals/CreateSettingModal';
import type { Project, LocationProfile } from '@/types/projects';

interface SettingProfilesSectionProps {
  project: Project;
}

interface SettingCardProps {
  setting: LocationProfile;
  onEdit: (setting: LocationProfile) => void;
  onDelete: (setting: LocationProfile) => void;
  onToggleDefault: (setting: LocationProfile) => void;
}

const SettingCard: React.FC<SettingCardProps> = ({
  setting,
  onEdit,
  onDelete,
  onToggleDefault
}) => {
  const [showActions, setShowActions] = useState(false);

  // Get the first reference image for preview
  const previewImage = setting.referenceImageUrls[0];

  return (
    <div className="relative group">
      <HolographicCard
        glow={setting.isDefault ? 'cyan' : 'blue'}
        intensity={setting.isDefault ? 'medium' : 'subtle'}
        className="h-full"
      >
        <CardContent className="p-4">
          {/* Preview Image */}
          <div className="aspect-[9/16] bg-black/50 border border-white/10 rounded-lg mb-3 overflow-hidden relative">
            {previewImage ? (
              <img
                src={previewImage}
                alt={setting.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <ImageIcon className="w-8 h-8 text-white/20" />
              </div>
            )}

            {/* Default Star Badge */}
            {setting.isDefault && (
              <div className="absolute top-2 left-2 bg-cyan-500/20 border border-cyan-500/50 rounded-full p-1">
                <Star className="w-3 h-3 text-cyan-400 fill-current" />
              </div>
            )}

            {/* Image Count Badge */}
            {setting.referenceImageUrls.length > 0 && (
              <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full px-2 py-1">
                <span className="text-xs text-white/80 font-mono">
                  {setting.referenceImageUrls.length}
                </span>
              </div>
            )}
          </div>

          {/* Setting Info */}
          <div className="mb-3">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-sm font-medium text-white/90 truncate flex-1">
                {setting.name}
              </h3>

              {/* Actions Menu */}
              <div className="relative ml-2">
                <button
                  onClick={() => setShowActions(!showActions)}
                  className={cn(
                    'p-1 rounded opacity-0 group-hover:opacity-100 transition-all duration-300',
                    'hover:bg-white/10 text-white/60 hover:text-white/80',
                    showActions && 'opacity-100 bg-white/10'
                  )}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>

                {showActions && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowActions(false)}
                    />
                    <div className="absolute right-0 top-8 z-20 bg-black/90 backdrop-blur-xl border border-white/20 rounded-lg shadow-xl min-w-[140px]">
                      <div className="py-2">
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onEdit(setting);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center gap-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onToggleDefault(setting);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center gap-2"
                        >
                          {setting.isDefault ? (
                            <>
                              <StarOff className="w-4 h-4" />
                              Unset Default
                            </>
                          ) : (
                            <>
                              <Star className="w-4 h-4" />
                              Set Default
                            </>
                          )}
                        </button>
                        <div className="border-t border-white/10 my-1" />
                        <button
                          onClick={() => {
                            setShowActions(false);
                            onDelete(setting);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {setting.notes && (
              <p className="text-xs text-white/50 line-clamp-2">
                {setting.notes}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button className="w-full btn-future text-blue-400 border-blue-400/30 hover:bg-blue-400/10 text-xs">
              <Upload className="w-3 h-3 mr-2" />
              Upload Images
            </button>
          </div>
        </CardContent>
      </HolographicCard>
    </div>
  );
};

const SettingProfilesSection: React.FC<SettingProfilesSectionProps> = ({ project }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: locationsData, isLoading } = useLocationProfilesQuery(project.id);
  const updateLocationMutation = useUpdateLocationProfileMutation();
  const deleteLocationMutation = useDeleteLocationProfileMutation();

  const locations: LocationProfile[] = Array.isArray(locationsData) ? locationsData :
    locationsData?.locations ?? [];

  const handleEdit = (location: LocationProfile) => {
    // TODO: Implement edit modal
    console.log('Edit location:', location);
  };

  const handleDelete = async (location: LocationProfile) => {
    if (window.confirm(`Are you sure you want to delete "${location.name}"?`)) {
      try {
        await deleteLocationMutation.mutateAsync({
          projectId: project.id,
          locationId: location.id
        });
      } catch (error) {
        console.error('Failed to delete location:', error);
      }
    }
  };

  const handleToggleDefault = async (location: LocationProfile) => {
    try {
      await updateLocationMutation.mutateAsync({
        projectId: project.id,
        locationId: location.id,
        data: { isDefault: !location.isDefault }
      });
    } catch (error) {
      console.error('Failed to update location default status:', error);
    }
  };

  return (
    <div>
      <HolographicCard glow="purple" intensity="medium">
        <CardHeader
          title="LOCATION PROFILES"
          subtitle={`${locations.length} locations`}
          icon={<MapPin className="w-5 h-5" />}
          actions={
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-future text-purple-400 border-purple-400/50 hover:bg-purple-400/10 text-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Location
            </button>
          }
        />

        <CardContent>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-[9/16]" />
                  <Skeleton className="h-4" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ))}
            </div>
          ) : locations.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {locations.map((location) => (
                <SettingCard
                  key={location.id}
                  setting={location}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleDefault={handleToggleDefault}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <MapPin className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="text-lg font-light text-white/70 mb-2">
                No Locations Yet
              </h3>
              <p className="text-white/40 mb-6 max-w-md mx-auto">
                Create location profiles with reference images to define locations and environments for your content.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-future text-purple-400 border-purple-400/50 hover:bg-purple-400/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Location
              </button>
            </div>
          )}
        </CardContent>
      </HolographicCard>

      {/* Create Setting Modal */}
      <CreateSettingModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        project={project}
      />
    </div>
  );
};

export default SettingProfilesSection;